import React, { useState } from "react";
import { Input, Button, Space, Popconfirm, message, Tag, Switch, Tooltip } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, PictureOutlined } from "@ant-design/icons";
import BannerServ from "../../service/BannerServ";
import AddBanner from "../../component/AddBanner";
import Breadcrumbs from "../../component/Breadcrumbs";
import { ResetButton } from "../../component/ui/ActionButtons";
import DataTable from "../../component/ui/DataTable";
import { getImageUrl } from "../../component/SafeImage";
import dayjs from "dayjs";
import { useAdminTable } from "../hooks/useAdminTable";

const Banners = () => {
    const [addBannerModal, setAddBannerModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [bannerDetails, setBannerDetails] = useState(null);

    const fetchBanners = async (params) => {
        const query = {
            limit: params.limit,
            page: params.page,
            allStatus: true
        };
        if (params.search) query.search = params.search;

        const res = await BannerServ.index(query);
        return { data: res?.data || [], total: res?.total || 0 };
    };

    const {
        data: banners,
        total,
        loading,
        params,
        setParams,
        handleSearch,
        refresh
    } = useAdminTable(fetchBanners, "admin_banners", {
        page: 1, limit: 10, search: ""
    });

    const handleAddBanner = () => {
        setBannerDetails(null);
        setIsEditing(false);
        setAddBannerModal(true);
    };

    const handleEdit = (record) => {
        setBannerDetails(record);
        setIsEditing(true);
        setAddBannerModal(true);
    };

    const handleDelete = async (id) => {
        try {
            await BannerServ.remove(id);
            message.success("Banner removed successfully");
            refresh();
        } catch (error) {
            message.error("Failed to remove banner");
        }
    };

    const toggleLive = async (record) => {
        try {
            const newStatus = !record.isLive;
            await BannerServ.update(record._id, { isLive: newStatus });
            message.success(`Banner is now ${newStatus ? 'Live' : 'Dead'}`);
            refresh();
        } catch (error) {
            message.error("Failed to update live status");
        }
    };

    const columns = [
        {
            title: "Banner Image",
            dataIndex: "imageUrl",
            key: "imageUrl",
            width: 100,
            align: "center",
            render: (url) => <img src={getImageUrl(url)} style={{ width: 100, height: 50, borderRadius: 8, objectFit: 'cover', border: '1px solid #f0f0f0' }} alt="banner" />
        },
        {
            title: "Banner Details",
            key: "details",
            width: 90,
            render: (text, record) => (
                <div style={{ display: "flex", flexDirection: "column" }}>
                    <span style={{ fontWeight: 700, color: "#1e293b", fontSize: "14px" }}>{record.title || "Untitled Banner"}</span>
                </div>
            ),
        },
        {
            title: "Target Platform",
            key: "targetPlatform",
            width: 120,
            render: (_, record) => {
                const plat = record.targetPlatform || 'all';
                if (plat === 'mobile') return <Tag color="gold">📱 Mobile Only</Tag>;
                if (plat === 'web') return <Tag color="cyan">🖥️ Web Only</Tag>;
                return <Tag color="blue">🌐 Both (Web & App)</Tag>;
            }
        },
        {
            title: "Placement / Screens",
            key: "placement",
            render: (_, record) => (
                <Space size={4} wrap>
                    {record.position === 'HOME_RIGHT' ? (
                        <Tag color="purple">HOME RIGHT (2:1)</Tag>
                    ) : record.position === 'HOME_TOP_SLIDER' ? (
                        <Tag color="green">TOP SLIDER (3:1)</Tag>
                    ) : record.position === 'HOME_TOP_CAROUSEL' ? (
                        <Tag color="gold">TOP CAROUSEL (2:1)</Tag>
                    ) : record.position === 'HOME_MID_SECTION' ? (
                        <Tag color="magenta">MID IN-FEED (3:1)</Tag>
                    ) : record.position === 'CATEGORY_TOP' ? (
                        <Tag color="orange">CATEGORY TOP (3:1)</Tag>
                    ) : record.position === 'CART_PROMO' ? (
                        <Tag color="pink">CART PROMO (3:1)</Tag>
                    ) : record.position === 'CHECKOUT_PROMO' ? (
                        <Tag color="geekblue">CHECKOUT PROMO (3:1)</Tag>
                    ) : (
                        record.screens?.map(s => <Tag key={s} color="orange">{s.toUpperCase()}</Tag>)
                    )}
                    {record.redirectType && record.redirectType !== 'none' && (
                        <Tag color="default">🔗 {record.redirectType.toUpperCase()}</Tag>
                    )}
                </Space>
            )
        },
        {
            title: "Schedule",
            key: "schedule",
            width: 100,
            render: (_, record) => (
                <div style={{ fontSize: 12 }}>
                    <div> {dayjs(record.startDate).format('DD MMM YYYY')}</div>
                    <div> {dayjs(record.endDate).format('DD MMM YYYY')}</div>
                </div>
            )
        },
        {
            title: "Priority",
            dataIndex: "priority",
            key: "priority",
            width: 50,
            sorter: (a, b) => a.priority - b.priority,
        },
        {
            title: "Active",
            dataIndex: "isActive",
            key: "isActive",
            width: 80,
            render: (isActive) => (
                <Tag color={isActive ? "blue" : "default"}>{isActive ? "Active" : "Inactive"}</Tag>
            )
        },
        {
            title: "Status",
            dataIndex: "isLive",
            key: "isLive",
            width: "100px",
            align: "center",
            render: (isLive, record) => (
                <div style={{
                    background: isLive ? "#f0fdf4" : "#fef2f2",
                    border: `1px solid ${isLive ? "#dcfce7" : "#fee2e2"}`,
                    borderRadius: "8px",
                    padding: "4px 8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "8px",
                    minWidth: "110px"
                }}>
                    <span style={{
                        fontSize: "11px",
                        fontWeight: 700,
                        color: isLive ? "#22c55e" : "#ef4444",
                        textTransform: "uppercase"
                    }}>
                        {isLive ? "Online" : "Offline"}
                    </span>
                    <Switch
                        size="small"
                        checked={isLive}
                        onChange={() => toggleLive(record)}
                    />
                </div>
            ),
        },
        {
            title: "Actions",
            key: "actions",
            width: "120px",
            align: "center",
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="Edit">
                        <Button
                            type="text"
                            icon={<EditOutlined style={{ color: "#4776E6", fontSize: "16px" }} />}
                            onClick={() => handleEdit(record)}
                        />
                    </Tooltip>
                    <Popconfirm title="Remove this banner?" onConfirm={() => handleDelete(record._id)}>
                        <Tooltip title="Remove">
                            <Button type="text" danger icon={<DeleteOutlined style={{ fontSize: "16px" }} />} />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const topRef = React.useRef(null);

    return (
        <div className="pcu-padding-24" ref={topRef}>
            <div className="pcu-margin-bottom-16">
                <Breadcrumbs />
            </div>

            <div className="banners-container" style={{ padding: "24px", background: "white", borderRadius: "16px", boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
                {/* Header Section */}
                <div className="banners-header" style={{ marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                        <div
                            style={{
                                width: "48px",
                                height: "48px",
                                background: "#fff",
                                borderRadius: "12px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                                border: "1px solid #f1f5f9",
                            }}
                        >
                            <PictureOutlined style={{ fontSize: "22px", color: "#4776E6" }} />
                        </div>
                        <div>
                            <h1 style={{ margin: 0, fontSize: "20px", fontWeight: 800, color: "#1a1d2e", letterSpacing: "-0.5px" }}>Banner Management</h1>
                            <p style={{ margin: 0, fontSize: "12px", color: "#9ca3af", fontWeight: 500 }}>{total} banners configured in system</p>
                        </div>
                    </div>
                    <Button
                        type="primary"
                        onClick={handleAddBanner}
                        style={{
                            height: "40px",
                            borderRadius: "10px",
                            background: "linear-gradient(135deg, #FF8800 0%, #FF5E00 100%)",
                            border: "none",
                            padding: "0 20px",
                            fontSize: "13px",
                            fontWeight: 700,
                            boxShadow: "0 4px 12px rgba(255, 136, 0, 0.3)",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                        }}
                    >
                        <PlusOutlined style={{ fontSize: "16px" }} />
                        Add New Banner
                    </Button>
                </div>

                {/* Filters */}
                <div className="filter-section" style={{ marginBottom: "24px", display: "grid", gridTemplateColumns: "1fr auto", gap: "12px", alignItems: "flex-end" }}>
                    <div style={{ maxWidth: "400px" }}>
                        <span style={{ color: "#94a3b8", fontSize: "10px", fontWeight: 800, letterSpacing: "0.5px", textTransform: "uppercase", display: "block", marginBottom: "8px" }}>Search Banners</span>
                        <div style={{ position: "relative" }}>
                            <SearchOutlined style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", zIndex: 1 }} />
                            <Input
                                placeholder="Search by title..."
                                value={params.search}
                                onChange={e => handleSearch(e.target.value)}
                                style={{ height: "40px", paddingLeft: "36px", borderRadius: "10px", border: "1px solid #e2e8f0" }}
                            />
                        </div>
                    </div>
                    <ResetButton onClick={() => setParams({ page: 1, limit: 10, search: "" })} text="Reset Filters" />
                </div>

                <div className="pcu-border-top pcu-margin-x-n24">
                    <DataTable
                        data={banners}
                        columns={columns}
                        rowKey="_id"
                        loading={loading}
                        pagination={{
                            current: params.page,
                            pageSize: params.limit,
                            total: total,
                            showTotal: (total) => `Total ${total} items`,
                            onChange: (p) => {
                                setParams(prev => ({ ...prev, page: p }));
                                topRef.current?.scrollIntoView({ behavior: 'smooth' });
                            },
                            onShowSizeChange: (_, size) => setParams(prev => ({ ...prev, limit: size, page: 1 }))
                        }}
                    />
                </div>

                {addBannerModal && (
                    <AddBanner
                        open={addBannerModal}
                        isEditing={isEditing}
                        bannerDetails={bannerDetails}
                        refreshBanners={(r) => {
                            setAddBannerModal(false);
                            if (r) refresh();
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export default Banners;
